import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { getPaymentStatus } from "../lib/apiClient";
import { BUSINESS } from "../data";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error

  useEffect(() => {
    let tries = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (!sessionId) { setState("error"); return; }
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") { setState("paid"); return; }
        if (res.status === "expired") { setState("error"); return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries >= MAX_TRIES) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-[100svh] bg-[#030303] flex items-center justify-center px-5">
      <div className="max-w-lg w-full border border-zinc-800 bg-[#080808] p-10 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-[#FF3B30] mx-auto mb-6" size={40} />
            <h1 className="font-display text-2xl uppercase tracking-tight">Confirming your deposit…</h1>
            <p className="text-zinc-400 mt-3">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 border border-[#FF3B30] flex items-center justify-center mx-auto mb-6">
              <Check className="text-[#FF3B30]" size={30} />
            </div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-[#FF3B30] mb-2">Booking confirmed</p>
            <h1 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight">You're locked in.</h1>
            <p className="text-zinc-400 mt-4">
              Your deposit is paid and your slot is secured. We've emailed your confirmation — the {BUSINESS.short} team will see you on the day. Balance is settled in person.
            </p>
            <Link to="/"><Button data-testid="success-home-btn" className="mt-8 rounded-none bg-white text-black hover:bg-zinc-200 font-display uppercase tracking-widest">Back home</Button></Link>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-16 h-16 border border-zinc-700 flex items-center justify-center mx-auto mb-6">
              <X className="text-zinc-400" size={30} />
            </div>
            <h1 className="font-display text-2xl uppercase tracking-tight">We couldn't confirm payment</h1>
            <p className="text-zinc-400 mt-3">If you were charged, don't worry — call us on <a className="text-[#FF3B30]" href={`tel:${BUSINESS.phoneRaw}`}>{BUSINESS.phone}</a> and we'll sort it out.</p>
            <Link to="/"><Button className="mt-8 rounded-none bg-white text-black hover:bg-zinc-200 font-display uppercase tracking-widest">Back home</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
